import { Pagination, SuccessResponse } from "common/common.interface";
import { useSnack } from "common/hooks";
import { useInfiniteQueryWithLoading } from "common/hooks/src/api/useInfiniteQueryWithLoading";
import { useMutationWithLoading } from "common/hooks/src/api/useMutationWithLoading";
import { useQueryWithLoading } from "common/hooks/src/api/useQueryWithLoading";
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useQueryClient,
} from "react-query";
import { useNavigate } from "react-router-dom";
import { CERFAT_ROUTES } from "./cerfat.api.enum";
import { getCerfatBackend } from "./backends";
import { Cerfat, CreateCerfatPayload } from "modules/cerfat/types/cerfat.interface";

export const useGetCerfat = (id: string | undefined): UseQueryResult<Cerfat> => {
  const setSnack = useSnack();
  const getCerfat = async (): Promise<Cerfat> => {
    const backend = await getCerfatBackend();
    return await backend.getCerfat(id!);
  };
  return useQueryWithLoading(CERFAT_ROUTES.GET_ONE + id, getCerfat, {
    enabled: !!id,
    onError() {
      setSnack({ title: "Erreur interne!", severityType: "error" });
    },
  });
};

export const useCreateCerfat = (): UseMutationResult<SuccessResponse, Error, CreateCerfatPayload> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutationWithLoading(
    async (payload: CreateCerfatPayload) => {
      const backend = await getCerfatBackend();
      return await backend.createCerfat(payload);
    },
    {
      onSuccess: () => {
        setSnack({ title: "CERFA créé avec succès!", severityType: "success" });
        queryClient.invalidateQueries([CERFAT_ROUTES.GET_ALL]);
        navigate("/admin/cerfa");
      },
      onError() {
        setSnack({ title: "Erreur interne!", severityType: "error" });
      },
    }
  );
};

export const useUpdateCerfat = (id: string | undefined): UseMutationResult<SuccessResponse, Error, CreateCerfatPayload> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutationWithLoading(
    async (payload: CreateCerfatPayload) => {
      const backend = await getCerfatBackend();
      return await backend.updateCerfat(id!, payload);
    },
    {
      onSuccess: () => {
        setSnack({ title: "CERFA mis à jour!", severityType: "success" });
        queryClient.invalidateQueries([CERFAT_ROUTES.GET_ALL]);
        queryClient.invalidateQueries([CERFAT_ROUTES.GET_ONE + id]);
        navigate("/admin/cerfa");
      },
      onError() {
        setSnack({ title: "Erreur interne!", severityType: "error" });
      },
    }
  );
};

export const useGetCerfats = (
  searchName: string | undefined
): UseInfiniteQueryResult<Pagination<Cerfat>> => {
  const setSnack = useSnack();

  const getCerfats = async ({ pageParam = 1 }): Promise<Pagination<Cerfat>> => {
    const cerfatBackend = await getCerfatBackend();
    return await cerfatBackend.getAllCerfats(pageParam, 10, searchName);
  };

  return useInfiniteQueryWithLoading(
    CERFAT_ROUTES.GET_ALL + searchName,
    getCerfats,
    {
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useGenerateDocuments = (
  id: string | undefined
): UseMutationResult<SuccessResponse, Error, string[]> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const generateDocuments = async (
    payload: string[]
  ): Promise<SuccessResponse> => {
    const CommandeBackend = await getCerfatBackend();
    return await CommandeBackend.generateDocuments(payload);
  };

  return useMutationWithLoading(
    (payload: string[]) => generateDocuments(payload),
    {
      onSuccess: (data) => {
        setSnack({
          title: "Documents générés avec succès!",
          severityType: "success",
        });

        queryClient.invalidateQueries([CERFAT_ROUTES.GET_ALL]);
        queryClient.setQueryData([CERFAT_ROUTES.GET_ALL], data);

        navigate("/admin/cerfat");
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useDownloadCerfa = (id: string | undefined) => {
  const setSnack = useSnack();

  const download = async () => {
    if (!id) return;
    try {
      const backend = await getCerfatBackend();
      const pdfData = await backend.downloadCerfa(id);
      const blob = new Blob([pdfData], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cerfa-15497-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      setSnack({ title: "Erreur lors du téléchargement!", severityType: "error" });
    }
  };

  return { download };
};
